/**
 * Bubble loader.
 *
 * Lives at a stable URL: https://embed.djinorix.ai/v1/bubble.js
 *
 * Parses the host page's <script data-tenant-key="..."> tag, fetches
 * the public bubble config, and dynamically imports the app bundle.
 * That's it — every visitor pays for this script, the heavier app
 * bundle is fetched only when we know the tenant is enabled.
 *
 * Embed snippet on the tenant's site:
 *
 *   <script
 *     async
 *     src="https://embed.djinorix.ai/v1/bubble.js"
 *     data-tenant-key="abc123..."
 *     data-config-api-url="https://marketing-api.djinorix.ai/graphql"
 *     data-config-api-key="da2-..."
 *   ></script>
 */

import { fetchBubbleConfig } from './config-fetch';
import { findScriptTag, readLoaderConfig } from './script-tag';
import type { PublicBubbleConfig } from '../types/public-config';

declare global {
  interface Window {
    __TRAVELRR_BUBBLE_LOADED__?: boolean;
  }
  interface ImportMetaEnv {
    readonly BUBBLE_ENV?: string;
  }
}

const BUBBLE_ENV: string = import.meta.env.BUBBLE_ENV ?? 'dev';

interface BubbleAppModule {
  mount: (config: PublicBubbleConfig) => void;
}

async function boot(): Promise<void> {
  // Idempotent — if the snippet ends up on the page twice (CMS quirks),
  // we only ever mount once.
  if (window.__TRAVELRR_BUBBLE_LOADED__) return;
  window.__TRAVELRR_BUBBLE_LOADED__ = true;

  const scriptTag = findScriptTag();
  if (!scriptTag) {
    warn('could not locate the bubble script tag — aborting');
    return;
  }

  // HTTPS gate. In prod the bubble must only ever render over HTTPS —
  // we send a per-tenant secret (tenantKey) on every request, so an
  // http:// origin would leak it on the wire. In dev we leave it
  // permissive so localhost and `file://` smoke tests keep working.
  if (BUBBLE_ENV !== 'dev' && window.location.protocol !== 'https:') {
    warn(
      `refusing to load on ${window.location.protocol}// — bubble requires HTTPS in production`,
    );
    return;
  }

  let loaderConfig;
  try {
    loaderConfig = readLoaderConfig(scriptTag);
  } catch (err) {
    warn(`invalid embed snippet: ${(err as Error).message}`);
    return;
  }

  let config: PublicBubbleConfig;
  try {
    config = await fetchBubbleConfig(loaderConfig);
  } catch (err) {
    warn(`failed to fetch bubble config: ${(err as Error).message}`);
    return;
  }

  if (!config.enabled) {
    // Tenant has the bubble disabled (e.g. paused, downgraded). Stay quiet.
    return;
  }

  if (!isOriginAllowed(config.allowedDomains)) {
    warn(
      `origin "${window.location.origin}" not in tenant allowedDomains — refusing to render`,
    );
    return;
  }

  let mod: BubbleAppModule;
  try {
    mod = (await import(/* @vite-ignore */ config.appBundleUrl)) as BubbleAppModule;
  } catch (err) {
    warn(`failed to load app bundle: ${(err as Error).message}`);
    return;
  }

  if (typeof mod.mount !== 'function') {
    warn('app bundle has no mount() export — aborting');
    return;
  }

  mod.mount(config);
}

function warn(message: string): void {
  // Keep loader logs prefixed and silent in production unless debug is on.
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(`[travelrr-bubble] ${message}`);
  }
}

/**
 * Lightweight origin allowlist check. Empty list means "any host".
 * Stored entries can be bare hostnames (`example.com`), full URLs
 * (`https://example.com`), or trailing-slash variants — strip the
 * scheme and any trailing slash on both sides for an exact host match.
 *
 * No wildcard support in MVP. Subdomains have to be listed explicitly.
 */
function isOriginAllowed(allowList: string[]): boolean {
  if (!allowList || allowList.length === 0) return true;
  const current = stripScheme(window.location.origin).replace(/\/$/, '');
  return allowList.some(entry => {
    if (!entry) return false;
    const normalised = stripScheme(entry).replace(/\/$/, '');
    return normalised === current;
  });
}

function stripScheme(value: string): string {
  if (value.startsWith('https://')) return value.slice('https://'.length);
  if (value.startsWith('http://')) return value.slice('http://'.length);
  return value;
}

// Defer until DOMContentLoaded so we never block the host page.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void boot());
} else {
  void boot();
}
